import {
  renderTranslationString,
  TFunctionType
} from 'shared/entities/localization';

import { VarSpecial } from '../../vars';
import { getVarActualName } from '../../vars/utils/getVarActualName';
import { ActualNameCreator } from '../../block/common';
import { IRootStore } from '../../store/rootStore';
import {
  ConditionMode,
  mapConditionCmpToItem,
  mapConditionModeToTitle
} from '../server';
import {
  IConditionParamsModel,
  IVarBaseCondition,
  ISqBaseCondition
} from '../client';

export const concatConditions = ({
  conditions,
  mode,
  rootStore,
  t
}: {
  conditions: IVarBaseCondition<string, string>[];
  mode: ConditionMode;
  rootStore: IRootStore;
  t: TFunctionType;
}): string => {
  if (!conditions.length) {
    return '';
  }

  const conditionStrings = conditions.map((condition) => {
    const specialVar = rootStore.variablesStore.getSpecialVariableByKey(
      condition.key
    );
    let key = condition.key;

    if (specialVar) {
      switch (condition.key) {
        case VarSpecial.channelOid: {
          key = t('vars.extraNameKeys.channel', {
            ns: 'entities'
          });
          break;
        }
        case VarSpecial.userId:
        case VarSpecial.userOid: {
          key = t('vars.extraNameKeys.user', {
            ns: 'entities'
          });
          break;
        }
        default: {
          key = specialVar.title(t);
        }
      }
    }

    return `${key} ${renderTranslationString(
      mapConditionCmpToItem[condition.cmp]?.shortcut || '',
      t
    )} ${condition.value}`;
  });

  return conditionStrings.join(` ${mapConditionModeToTitle[mode](t)} `);
};

export const concatVarConditions = ({
  conditions,
  mode,
  rootStore,
  t
}: {
  conditions: IVarBaseCondition<string, string>[];
  mode: ConditionMode;
  rootStore: IRootStore;
  t: TFunctionType;
}): string => {
  const actualConditions: IVarBaseCondition<string, string>[] = conditions.map(
    (condition) => {
      return {
        key: condition.key,
        cmp: condition.cmp,
        value: getVarActualName(condition, rootStore, t)
      };
    }
  );

  return concatConditions({
    conditions: actualConditions,
    mode,
    rootStore,
    t
  });
};

export const concatSqConditions = ({
  conditions,
  mode,
  t
}: {
  conditions: ISqBaseCondition[];
  mode: ConditionMode;
  t: TFunctionType;
}): string => {
  if (!conditions.length) {
    return '';
  }

  return conditions
    .map((condition) => condition.expr)
    .join(` ${mapConditionModeToTitle[mode](t)} `);
};

export const concatCommentAddConditions = ({
  conditions,
  mode,
  t
}: {
  conditions: IVarBaseCondition<VarSpecial.messageText, string>[];
  mode: ConditionMode;
  t: TFunctionType;
}): string => {
  if (!conditions.length) {
    return '';
  }

  const conditionStrings = conditions.map((condition) => {
    const key = t('condition.commentAddTitle', {
      ns: 'entities'
    });

    return `${key} ${renderTranslationString(
      mapConditionCmpToItem[condition.cmp]?.shortcut || '',
      t
    )} ${condition.value}`;
  });

  return conditionStrings.join(` ${mapConditionModeToTitle[mode](t)} `);
};

export const createConditionParamsActualNameBase = ({
  messageConditions,
  varConditions,
  sqConditions,
  rootStore,
  t
}: {
  rootStore: IRootStore;
  messageConditions: {
    conditions: IVarBaseCondition<VarSpecial.messageText, string>[];
    mode: ConditionMode;
  };
  varConditions: {
    conditions: IVarBaseCondition<any, any>[];
    mode: ConditionMode;
  };
  sqConditions: {
    conditions: ISqBaseCondition[];
    mode: ConditionMode;
  };
  t: TFunctionType;
}): string => {
  const messagePreview = concatConditions({
    ...messageConditions,
    rootStore,
    t
  });

  const varPreview = concatVarConditions({
    ...varConditions,
    rootStore,
    t
  });

  const sqPreview = concatSqConditions({ ...sqConditions, t });

  return [messagePreview, varPreview, sqPreview].filter(Boolean).join(
    ` ${t('condition.mode.and', {
      ns: 'entities'
    })} `
  );
};

export const createConditionParamsActualName: ActualNameCreator<
  IConditionParamsModel
> = ({ params, rootStore, t }) => {
  const messagePreview = concatConditions({
    conditions: params.messageConditions.conditions.items,
    mode: params.messageConditions.mode,
    rootStore,
    t
  });

  const varPreview = concatVarConditions({
    conditions: params.varConditions.conditions.items.map((item) => {
      return {
        key: item.data.data.key,
        cmp: item.data.data.cmp,
        value: item.actualValue
      };
    }),
    mode: params.varConditions.mode,
    rootStore,
    t
  });

  const sqPreview = concatSqConditions({
    conditions: params.sqConditions.conditions.items,
    mode: params.sqConditions.mode,
    t
  });

  return [messagePreview, varPreview, sqPreview].filter(Boolean).join(
    ` ${t('condition.mode.and', {
      ns: 'entities'
    })} `
  );
};

export const createCommentAddConditionParamsActualName: ActualNameCreator<
  IConditionParamsModel
> = ({ params, rootStore, t }) => {
  const messagePreview = concatCommentAddConditions({
    conditions: params.messageConditions.conditions.items,
    mode: params.messageConditions.mode,
    t
  });

  const varPreview = concatVarConditions({
    conditions: params.varConditions.conditions.items.map((item) => {
      return {
        key: item.data.data.key,
        cmp: item.data.data.cmp,
        value: item.actualValue
      };
    }),
    mode: params.varConditions.mode,
    rootStore,
    t
  });

  const sqPreview = concatSqConditions({
    conditions: params.sqConditions.conditions.items,
    mode: params.sqConditions.mode,
    t
  });

  return [messagePreview, varPreview, sqPreview].filter(Boolean).join(
    ` ${t('condition.mode.and', {
      ns: 'entities'
    })} `
  );
};
